import React, { useMemo, useState, useEffect, useRef } from "react";
import { Modal, ModalHeader, ModalBody, Button } from "reactstrap";
import { FaBackspace, FaTimesCircle, FaSearch } from "react-icons/fa";

/**
 * POSKeypadModal
 * - Touch keypad for POS tablets
 * - External keyboard supported
 *
 * Props:
 *  - isOpen: boolean
 *  - toggle: () => void
 *  - value: string
 *  - onChange: (newValue: string) => void
 *  - onSearch?: () => void
 *  - title?: string
 *  - maxLength?: number
 *  - showSearchButton?: boolean
 */
const POSKeypadModal = ({
  isOpen,
  toggle,
  value,
  onChange,
  onSearch,
  title = "Clavier",
  maxLength = 64,
  showSearchButton = true,
}) => {
  const inputRef = useRef(null);
  const [isSelected, setIsSelected] = useState(true);

  // Reset selection when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsSelected(true);

      // Focus invisible input to capture external keyboard
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // External keyboard input handler
  const handleKeyDown = (e) => {
    if (!e.key) return;

    if (e.key === "Backspace") {
      e.preventDefault();
      handleBackspace();
      return;
    }
    if (e.key === "Delete") {
      e.preventDefault();
      handleClear();
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      handleSearchOrConfirm();
      return;
    }

    const allowedKeys = /^[a-zA-Z0-9\.\-\/_ ]$/;
    if (allowedKeys.test(e.key)) {
      e.preventDefault();
      handleAppend(e.key);
    }
  };

  // Touch / programmatic handlers
  const handleAppend = (ch) => {
    if (isSelected) {
      onChange(ch);
      setIsSelected(false);
      return;
    }
    onChange(`${value || ""}${ch}`.slice(0, maxLength));
  };

  const handleBackspace = () => {
    if (isSelected) {
      onChange("");
      setIsSelected(false);
      return;
    }
    if (!value) return;
    onChange(value.slice(0, -1));
  };

  const handleClear = () => {
    onChange("");
    setIsSelected(false);
  };

  const handleSpace = () => handleAppend(" ");

  const handleSearchOrConfirm = () => {
    if (showSearchButton) onSearch?.();
    toggle();
  };

  useEffect(() => {
    if (!isOpen) return;

    const onWindowKeyDown = (e) => {
      if (e.key !== "Enter") return;
      e.preventDefault();
      e.stopPropagation();
      handleSearchOrConfirm();
    };

    window.addEventListener("keydown", onWindowKeyDown);
    return () => window.removeEventListener("keydown", onWindowKeyDown);
  }, [isOpen, value, showSearchButton]);

  // Keypad layout
  const rows = useMemo(
    () => [
      ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"],
      ["A", "Z", "E", "R", "T", "Y", "U", "I", "O", "P"],
      ["Q", "S", "D", "F", "G", "H", "J", "K", "L", "-"],
      ["W", "X", "C", "V", "B", "N", "M", ".", "/", "_"],
    ],
    []
  );

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="lg"
      centered
      autoFocus={false}
      returnFocusAfterClose={false}
    >
      <ModalHeader toggle={toggle}>{title}</ModalHeader>

      <ModalBody>
        {/* Invisible input to capture external keyboard */}
        <input
          ref={inputRef}
          type="text"
          autoComplete="off"
          onKeyDown={handleKeyDown}
          style={{
            position: "absolute",
            opacity: 0,
            pointerEvents: "none",
            height: 0,
            width: 0,
          }}
        />

        {/* Display */}
        <div
          className="pos-keypad-display"
          style={{
            border: "2px solid",
            borderColor: isSelected ? "#2563eb" : "#e5e7eb",
            background: isSelected ? "#eef2ff" : "#fff",
            borderRadius: 12,
            padding: "14px 16px",
            fontSize: 20,
            fontWeight: 700,
            marginBottom: 12,
            minHeight: 56,
            userSelect: "none",
          }}
        >
          {value || <span className="text-muted">...</span>}
        </div>

        {/* Keypad */}
        <div className="pos-keypad-grid" style={{ display: "grid", gap: 10 }}>
          {rows.map((row, rowIndex) => (
            <div
              key={rowIndex}
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(10, 1fr)",
                gap: 10,
              }}
            >
              {row.map((key) => (
                <Button
                  key={key}
                  color="light"
                  className="fw-bold"
                  style={{ height: 56, borderRadius: 14, fontSize: 18 }}
                  onClick={() => handleAppend(key)}
                >
                  {key}
                </Button>
              ))}
            </div>
          ))}

          {/* Actions row */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "2fr 1fr 1fr 1fr",
              gap: 10,
              marginTop: 6,
            }}
          >
            <Button
              color="secondary"
              className="fw-bold"
              style={{ height: 56, borderRadius: 14, fontSize: 18 }}
              onClick={handleSpace}
            >
              ESPACE
            </Button>

            <Button
              color="warning"
              className="fw-bold"
              style={{ height: 56, borderRadius: 14, fontSize: 18 }}
              onClick={handleBackspace}
              title="Effacer"
            >
              <FaBackspace className="me-2" />
              DEL
            </Button>

            <Button
              color="danger"
              className="fw-bold"
              style={{ height: 56, borderRadius: 14, fontSize: 18 }}
              onClick={handleClear}
              title="Vider"
            >
              <FaTimesCircle className="me-2" />
              CLEAR
            </Button>

            {showSearchButton ? (
              <Button
                color="primary"
                className="fw-bold"
                style={{ height: 56, borderRadius: 14, fontSize: 18 }}
                onClick={handleSearchOrConfirm}
              >
                <FaSearch className="me-2" />
                OK
              </Button>
            ) : (
              <Button
                color="primary"
                className="fw-bold"
                style={{ height: 56, borderRadius: 14, fontSize: 18 }}
                onClick={toggle}
              >
                OK
              </Button>
            )}
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default POSKeypadModal;
